import { useEffect, useState } from 'react';

/**
 * Resolves a DOM element by selector for onboarding components.
 *
 * This is an escape hatch for cases where the actual onboarding anchor is
 * inside wrapped children and cannot be wrapped directly.
 *
 * The target can appear after async rendering, so we observe the document
 * until the element exists.
 */
export function useOnboardingTargetSelector(targetSelector?: string) {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!targetSelector) {
      setTargetElement(null);
      return;
    }

    const resolveTargetElement = () => {
      try {
        setTargetElement(document.querySelector<HTMLElement>(targetSelector));
      } catch {
        setTargetElement(null);
      }
    };

    resolveTargetElement();

    const observer = new MutationObserver(resolveTargetElement);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [targetSelector]);

  return targetElement;
}
